/**
 * Day 4, content collection, from that stage onward: cohort and source
 * readiness, the bounded live collection or the Owner-assisted structured
 * import, the collection attempts, and the collected posts with their
 * sampled comments.
 */

import type { ReactNode } from 'react';

import { ANALYSIS_STAGES } from '@mouja/domain/analysis';

import {
  ApiFailure,
  collectAnalysisContent,
  importAnalysisContent,
  newIdempotencyKey,
  type AnalysisDay4State,
  type AnalysisRunSummary,
} from '../../api.js';
import { Button, EmptyState, InlineAlert, Tag, TextAreaField } from '../../ui.js';
import { SectionHeading } from '../common.js';
import type { AnalysisRunPageCopy } from './run-page-copy.js';
import { card } from './run-page-styles.js';
import { toneFor } from './run-state-labels.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { ReviewDrafts } from './useReviewDrafts.js';

function isStaleContentCollectionState(failure: unknown): boolean {
  if (!(failure instanceof ApiFailure) || failure.status !== 400) return false;
  return [
    'This run is not ready for content collection.',
    'This run is not ready for content collection or re-snapshotting.',
  ].includes(failure.detail ?? '');
}

export function ContentCollectionSection({
  id,
  run,
  day4,
  busy,
  mutate,
  drafts,
  copy,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly day4: AnalysisDay4State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly drafts: ReviewDrafts;
  readonly copy: AnalysisRunPageCopy;
}): ReactNode {
  const { contentImportJson, setContentImportJson } = drafts;
  if (
    ANALYSIS_STAGES.findIndex((stage) => stage === run.currentStage) <
    ANALYSIS_STAGES.indexOf('TARGET_AND_COMPETITOR_CONTENT_COLLECTION')
  ) {
    return null;
  }

  return (
    <section id="content-evidence" style={{ marginBottom: 'var(--space-lg)' }}>
      <SectionHeading>{copy.contentCollection}</SectionHeading>
      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <h3 style={{ font: 'var(--type-heading-sm)', margin: 0 }}>
          Cohort and source readiness
        </h3>
        {day4.readinessPreview.length === 0 ? (
          <InlineAlert
            tone="warning"
            title="The immutable four-Business cohort is incomplete."
          />
        ) : (
          <div className="mj-form-two-column">
            {day4.readinessPreview.map((member) => (
              <div
                key={member.businessId}
                style={{ ...card, background: 'var(--surface-subtle)' }}
              >
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Tag tone="info">{member.role}</Tag>
                  <Tag tone={toneFor(member.readiness)}>{member.readiness}</Tag>
                  {member.platform ? <Tag tone="info">{member.platform}</Tag> : null}
                </div>
                <strong>Business {member.businessId}</strong>
                {member.sourceUrl ? (
                  <p>
                    <a href={member.sourceUrl} target="_blank" rel="noreferrer">
                      {member.sourceUrl}
                    </a>
                  </p>
                ) : null}
                <p style={{ color: 'var(--text-muted)' }}>
                  {member.associationBasis ??
                    member.reason ??
                    'No association evidence.'}
                </p>
              </div>
            ))}
          </div>
        )}
        {run.currentStage === 'TARGET_AND_COMPETITOR_CONTENT_COLLECTION' ||
        run.currentStage === 'SHALLOW_CONTENT_ENRICHMENT' ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <Button
              variant="primary"
              busy={busy === 'collect-content'}
              disabled={day4.readinessPreview.some(
                (member) => member.readiness !== 'READY',
              )}
              onClick={() =>
                void mutate(
                  'collect-content',
                  () => collectAnalysisContent(id, newIdempotencyKey()),
                  isStaleContentCollectionState,
                )
              }
            >
              Collect bounded live corpus
            </Button>
            <details>
              <summary>Owner-assisted structured import (credential fallback)</summary>
              <p style={{ color: 'var(--text-muted)' }}>
                Paste the bounded provider-shaped JSON fixture. It uses the same
                normalizers, provenance artifacts, identities, snapshots and comment
                sampling as live collection.
              </p>
              <TextAreaField
                label="Structured Day-4 import JSON"
                value={contentImportJson}
                onChange={setContentImportJson}
                rows={8}
                maxLength={1_800_000}
              />
              <Button
                variant="outline"
                busy={busy === 'import-content'}
                disabled={!contentImportJson.trim()}
                onClick={() =>
                  void mutate('import-content', () => {
                    let parsed: unknown;
                    try {
                      parsed = JSON.parse(contentImportJson);
                    } catch {
                      throw new ApiFailure('invalid', {
                        status: 400,
                        detail: 'Import JSON is invalid.',
                      });
                    }
                    if (
                      typeof parsed !== 'object' ||
                      parsed === null ||
                      Array.isArray(parsed)
                    ) {
                      throw new ApiFailure('invalid', {
                        status: 400,
                        detail: 'Import JSON must be an object.',
                      });
                    }
                    return importAnalysisContent(
                      id,
                      newIdempotencyKey(),
                      parsed as Record<string, unknown>,
                    );
                  })
                }
              >
                Import bounded corpus
              </Button>
            </details>
          </div>
        ) : null}
      </div>

      {day4.attempts.length > 0 ? (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {day4.attempts.map((attempt) => (
            <div key={attempt.id} style={card}>
              <strong>{attempt.provider}</strong> · {attempt.resultState}
              <p style={{ color: 'var(--text-muted)' }}>
                {attempt.actualPostCount} / {attempt.requestedPostLimit} posts ·{' '}
                {attempt.actualCommentCount} comments ·{' '}
                {attempt.costMicros === null
                  ? 'cost unknown'
                  : `$${(Number(attempt.costMicros) / 1_000_000).toFixed(4)} ${attempt.costBasis}`}
              </p>
              {attempt.failureDetail ? (
                <InlineAlert tone="warning" title={attempt.failureDetail} />
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {day4.content.length > 0 ? (
        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>
            Collected posts ({day4.content.length})
          </h3>
          {day4.content.map((item) => (
            <article key={item.id} style={card}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Tag tone="info">{item.subjectRole}</Tag>
                <Tag tone="info">{item.platform}</Tag>
                {item.pinned === true ? <Tag tone="warning">PINNED</Tag> : null}
              </div>
              <strong>{item.businessName}</strong>
              <p>
                <a href={item.canonicalUrl} target="_blank" rel="noreferrer">
                  {item.canonicalUrl}
                </a>
              </p>
              <p>{item.caption ?? 'Caption unavailable'}</p>
              <p style={{ color: 'var(--text-muted)' }}>
                Published {item.publishedAt ?? 'unknown'} · observed {item.observedAt}
                <br />
                views {item.viewCount ?? 'unknown'} · likes{' '}
                {item.likeCount ?? 'unknown'} · comments{' '}
                {item.commentCount ?? 'unknown'} · shares {item.shareCount ?? 'unknown'}{' '}
                · saves {item.saveCount ?? 'unknown'}
              </p>
              {day4.commentSamples
                .filter((sample) => sample.contentItemId === item.id)
                .map((sample) => (
                  <details key={sample.id}>
                    <summary>
                      {sample.selectionReason} · {sample.samplingMethod} ·{' '}
                      {sample.comments.length} comments
                    </summary>
                    <ul>
                      {sample.comments.map((comment) => (
                        <li key={comment.id}>
                          <Tag tone="info">{comment.bucket}</Tag>{' '}
                          <bdi>{comment.text}</bdi>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}
            </article>
          ))}
        </div>
      ) : (
        <EmptyState title="No content corpus has been persisted yet." compact />
      )}
    </section>
  );
}
